import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { 
  Card, 
  Header, 
  Loader, 
  Message, 
  Statistic, 
  Grid, 
  Icon,
  Table, 
  Button, 
  Progress,
  Tab,
  Label
} from "semantic-ui-react";
import OrganizationSelect from 'organizations/OrganizationSelect';
import {
  SG_GET_DEBRIEF_SCHEDULE_SURVEY_STRUCTURE,
  SG_GET_DEBRIEF_SCHEDULE_DEEP_DIVE_REPORT,
  SG_POST_DEBRIEF_SCHEDULE_CATEGORY_DEEP_DIVE
} from "constants/actions";

const DeepDiveReport = ({ debriefId }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedOptionInfo, setSelectedOptionInfo] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [resetPageLoad, setResetPageLoad] = useState(0);

  const { 
    survey_structure, 
    deep_dive_report, 
    category_deep_dive,
    sync_info,
    pending 
  } = useSelector(
    (state) => ({
      survey_structure: state.debrief_schedule.survey_structure,
      deep_dive_report: state.debrief_schedule.deep_dive_report,
      sync_info: state.debrief_schedule.sync_info,
      category_deep_dive: state.debrief_schedule.category_deep_dive,
      pending: state.debrief_schedule.pending
    }),
    shallowEqual
  );

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganizationId(organizationId);
  };

  const handleResetQueryParams = () => {
    setSelectedOrganizationId(null);
  };

  useEffect(() => {
    if (debriefId) {
      // Build payload with optional organization filter
      const payload = { id: debriefId };
      if (selectedOrganizationId) {
        payload.organization = selectedOrganizationId;
      }

      // Fetch survey structure
      dispatch({
        type: SG_GET_DEBRIEF_SCHEDULE_SURVEY_STRUCTURE,
        payload
      });

      // Fetch deep dive report
      dispatch({
        type: SG_GET_DEBRIEF_SCHEDULE_DEEP_DIVE_REPORT,
        payload
      });
    }
  }, [dispatch, debriefId, selectedOrganizationId, resetPageLoad]);

  const handleOptionDeepDive = (optionId, optionName, categoryName) => {
    setSelectedOptionInfo({
      optionId,
      optionName,
      categoryName
    });
    dispatch({
      type: SG_POST_DEBRIEF_SCHEDULE_CATEGORY_DEEP_DIVE,
      payload: { 
        id: debriefId, 
        option_id: optionId 
      }
    });
    // Switch to category deep dive tab
    setActiveTab(1);
  };

  const toggleCategoryExpansion = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const renderSurveyStructure = () => {
    if (!survey_structure || !survey_structure.survey_structure) {
      return <Message info>No survey structure available</Message>;
    }

    const { survey_structure: structure } = survey_structure;

    console.log('Survey Structure:', sync_info);
    return (
      <div>
        <SurveyStructureHeader>
          <Header as="h3">Survey Structure: {structure.name}</Header>
          <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '15px' }}>
            Organization: {survey_structure.organization?.name}
          </div>
        </SurveyStructureHeader>
        
        {/* Sync Info Section */}
        {sync_info && (
          <Message 
            success={sync_info.synced && !sync_info.changed}
            warning={sync_info.synced && sync_info.changed}
            info={!sync_info.synced}
            icon
            style={{ marginBottom: '20px' }}
          >
            <Icon 
              name={sync_info.synced ? (sync_info.changed ? 'sync alternate' : 'check circle') : 'warning circle'} 
              loading={!sync_info.synced}
            />
            <Message.Content>
              <Message.Header>
                {sync_info.synced ? (
                  sync_info.changed ? 'Data Synced - Changes Detected' : 'Data Synced - Up to Date'
                ) : 'Syncing...'}
              </Message.Header>
              <Grid columns={4} style={{ marginTop: '10px' }}>
                <Grid.Row>
                  <Grid.Column>
                    <div style={{ fontSize: '0.9em' }}>
                      <strong>Status:</strong>{' '}
                      <Label 
                        size="small" 
                        color={sync_info.synced ? 'green' : 'yellow'}
                      >
                        {sync_info.synced ? 'Synced' : 'Pending'}
                      </Label>
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <div style={{ fontSize: '0.9em' }}>
                      <strong>Total Responses:</strong> {sync_info.total_responses || 0}
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <div style={{ fontSize: '0.9em' }}>
                      <strong>Added:</strong>{' '}
                      <Label size="small" color={sync_info.added_count > 0 ? 'blue' : 'grey'}>
                        {sync_info.added_count || 0}
                      </Label>
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <div style={{ fontSize: '0.9em' }}>
                      <strong>Removed:</strong>{' '}
                      <Label size="small" color={sync_info.removed_count > 0 ? 'red' : 'grey'}>
                        {sync_info.removed_count || 0}
                      </Label>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              {sync_info.changed && (
                <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#856404' }}>
                  ⚠️ Changes detected: {sync_info.added_count} responses added, {sync_info.removed_count} removed
                </div>
              )}
            </Message.Content>
          </Message>
        )}
        
        <Message info>
          <Message.Header>How to use:</Message.Header>
          <p>Click "Expand Options" to see individual options within each category, then click "Select This Category" to analyze employees who selected that specific option.</p>
          <p><strong>Note:</strong> The default report shows aggregated data for all options. Use option-specific deep dive to filter for specific selections.</p>
          <p><strong>Tip:</strong> Option IDs are shown for back-checking and debugging purposes.</p>
          <p><strong>🎯 Recommended:</strong> Use the "Select This Category" buttons in this section for reliable option-specific analysis with correct option IDs.</p>
        </Message>

        <SurveyStructureGrid>
          {structure.categories?.map((category) => {
            const isExpanded = expandedCategories.has(category.id);
            const hasOptions = category.options && category.options.length > 0;
            
            return (
              <CategoryStructureCard key={category.id}>
                <Card.Content>
                  <Card.Header>
                    {category.name}
                    <span style={{ float: 'right' }}>
                      <PriorityLabel priority={category.priority}>
                        {category.priority}
                      </PriorityLabel>
                    </span>
                  </Card.Header>
                  <Card.Meta>{category.level} • {category.options?.length || 0} options</Card.Meta>
                  
                  {/* Category-level actions */}
                  <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                    {hasOptions && (
                      <Button 
                        size="small" 
                        basic
                        icon={isExpanded ? 'chevron up' : 'chevron down'}
                        content={isExpanded ? 'Hide Options' : 'Expand Options'}
                        onClick={() => toggleCategoryExpansion(category.id)}
                      />
                    )}
                  </div>

                  {/* Options breakdown */}
                  {hasOptions && isExpanded && (
                    <div>
                      <Header as="h5" style={{ marginBottom: '10px', color: '#2185d0' }}>
                        Select Individual Options:
                      </Header>
                      <Table compact size="small">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell>Option ID</Table.HeaderCell>
                            <Table.HeaderCell>Option Name</Table.HeaderCell>
                            <Table.HeaderCell>Action</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {category.options.map((option, optionIndex) => {
                            const optionId = option.id || option.option_id || option.uuid || `${category.id}_${optionIndex}`;
                            
                            return (
                              <Table.Row key={optionId}>
                                <Table.Cell>
                                  <code>{optionId}</code>
                                </Table.Cell>
                                <Table.Cell>
                                  <strong>{option.name || option.option_name}</strong>
                                  {option.description && (
                                    <div style={{ fontSize: '0.9em', color: '#666', marginTop: '2px' }}>
                                      {option.description}
                                    </div>
                                  )}
                                </Table.Cell>
                                <Table.Cell>
                                  <Button 
                                    size="mini" 
                                    color="blue"
                                    onClick={() => {
                                      handleOptionDeepDive(
                                        optionId, 
                                        option.name || option.option_name, 
                                        category.name
                                      );
                                    }}
                                  >
                                    Select This Category
                                  </Button>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table>
                      <Message positive size="mini">
                        <p><strong>Tip:</strong> Click "Select This Category" to analyze only employees who selected that specific option.</p>
                      </Message>
                    </div>
                  )}
                </Card.Content>
              </CategoryStructureCard>
            );
          })}
        </SurveyStructureGrid>
      </div>
    );
  };

  const renderDeepDiveReport = () => {
    if (!deep_dive_report || !deep_dive_report.response) {
      return <Message info>No deep dive report available</Message>;
    }

    const { response } = deep_dive_report;

    // Debug logging
    console.log('Deep Dive Report Data:', response);
    console.log('Category Breakdown:', response.category_breakdown);

    return (
      <Card fluid>
        <Card.Content>
          <Card.Header>Deep Dive Report</Card.Header>
          <Card.Meta>{response.debrief_schedule_name} - {response.organization}</Card.Meta>
        </Card.Content>
        <Card.Content>
          <Grid columns={3} divided>
            <Grid.Row>
              <Grid.Column>
                <Statistic>
                  <Statistic.Value>{response.total_requests}</Statistic.Value>
                  <Statistic.Label>Total Requests</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic>
                  <Statistic.Value>{response.total_responses}</Statistic.Value>
                  <Statistic.Label>Total Responses</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic>
                  <Statistic.Value>{response.response_rate}%</Statistic.Value>
                  <Statistic.Label>Response Rate</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <Progress 
            percent={response.response_rate} 
            progress 
            success={response.response_rate >= 80}
            warning={response.response_rate >= 60 && response.response_rate < 80}
            error={response.response_rate < 60}
          >
            Response Rate: {response.response_rate}%
          </Progress>

          {response.category_breakdown && response.category_breakdown.length > 0 && (
            <ReportSection>
              <Header as="h4">Category Breakdown</Header>
              <Message info>
                <Message.Header>Category Options Analysis</Message.Header>
                <p>This section shows individual options within each category and their response metrics.</p>
              </Message>
              
              {response.category_breakdown.map((category, categoryIndex) => {
                const categoryName = category.category_name || category.name || `Category ${categoryIndex + 1}`;
                const hasOptions = category.options && category.options.length > 0;
                
                if (!hasOptions) return null;
                
                // Calculate category totals from options
                const totalRequested = category.options.reduce((sum, option) => sum + (option.requested_count || 0), 0);
                const totalResponded = category.options.reduce((sum, option) => sum + (option.responded_count || 0), 0);
                const avgResponseRate = totalRequested > 0 ? ((totalResponded / totalRequested) * 100).toFixed(2) : 0;
                
                return (
                  <div key={categoryIndex} style={{ marginBottom: '25px' }}>
                    <Header as="h5" style={{ color: '#2185d0', marginBottom: '10px' }}>
                      {categoryName}
                      <Label style={{ marginLeft: '10px' }} color={category.priority === 'primary' ? 'blue' : category.priority === 'secondary' ? 'teal' : 'orange'}>
                        {category.priority || 'N/A'}
                      </Label>
                    </Header>
                    
                    <Message size="mini" style={{ marginBottom: '10px' }}>
                      <strong>Category Summary:</strong> {totalRequested} requested, {totalResponded} responded ({avgResponseRate}% rate)
                    </Message>
                    
                    <Table compact celled striped>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Option Name</Table.HeaderCell>
                          <Table.HeaderCell>Requested</Table.HeaderCell>
                          <Table.HeaderCell>Responded</Table.HeaderCell>
                          <Table.HeaderCell>Response Rate</Table.HeaderCell>
                          <Table.HeaderCell>Action</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {category.options.map((option, optionIndex) => {
                          const optionName = option.option_name || option.name || `Option ${optionIndex + 1}`;
                          const requestedCount = option.requested_count || 0;
                          const respondedCount = option.responded_count || 0;
                          const responseRate = option.response_rate || 0;
                          const optionId = option.option_id;
                          
                          return (
                            <Table.Row key={optionIndex}>
                              <Table.Cell>
                                <strong>{optionName}</strong>
                                {optionId && (
                                  <div style={{ fontSize: '0.8em', color: '#666' }}>
                                    ID: <code>{optionId}</code>
                                  </div>
                                )}
                              </Table.Cell>
                              <Table.Cell>{requestedCount}</Table.Cell>
                              <Table.Cell>{respondedCount}</Table.Cell>
                              <Table.Cell>
                                <Progress 
                                  percent={responseRate} 
                                  size="tiny"
                                  success={responseRate >= 80}
                                  warning={responseRate >= 60 && responseRate < 80}
                                  error={responseRate < 60}
                                />
                                {responseRate}%
                              </Table.Cell>
                              <Table.Cell>
                                {requestedCount > 0 && optionId ? (
                                  <Button 
                                    size="mini" 
                                    color="blue"
                                    onClick={() => {
                                      handleOptionDeepDive(
                                        optionId, 
                                        optionName, 
                                        categoryName
                                      );
                                    }}
                                  >
                                    Deep Dive
                                  </Button>
                                ) : (
                                  <Label size="mini">No Data</Label>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table>
                  </div>
                );
              })}
            </ReportSection>
          )}

          {response.detailed_analysis && (
            <ReportSection>
              <Header as="h4">Detailed Analysis Report</Header>
              <Message info>
                <Message.Header>Option Analysis Overview</Message.Header>
                <p>This section shows performance metrics for options within each category. For option-specific deep dive analysis, use the "Select This Category" buttons in the Survey Structure section above.</p>
              </Message>
              <DetailedAnalysisContainer>
                {renderDetailedAnalysis(response.detailed_analysis)}
              </DetailedAnalysisContainer>
            </ReportSection>
          )}

          {response.filtered_responses && (
            <ReportSection>
              <Header as="h4">All Survey Responses</Header>
              {Array.isArray(response.filtered_responses) && response.filtered_responses.length > 0 ? (
                <>
                  <Message info>
                    <Message.Header>Complete Response Dataset</Message.Header>
                    <p>Showing all {response.filtered_responses.length} responses from the survey</p>
                  </Message>
                  
                  <Table celled striped>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Employee</Table.HeaderCell>
                        <Table.HeaderCell>Response Date</Table.HeaderCell>
                        <Table.HeaderCell>Status</Table.HeaderCell>
                        <Table.HeaderCell>Additional Data</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {response.filtered_responses.slice(0, 15).map((responseItem, index) => (
                        <Table.Row key={index}>
                          <Table.Cell>
                            {responseItem.employee_name || responseItem.user_id || `Employee ${index + 1}`}
                          </Table.Cell>
                          <Table.Cell>
                            {responseItem.created_at || responseItem.response_date || 'N/A'}
                          </Table.Cell>
                          <Table.Cell>
                            <Label color={responseItem.status === 'completed' ? 'green' : 'orange'}>
                              {responseItem.status || 'Completed'}
                            </Label>
                          </Table.Cell>
                          <Table.Cell>
                            {responseItem.additional_data ? (
                              <pre style={{ fontSize: '11px', margin: 0, maxHeight: '60px', overflow: 'auto' }}>
                                {JSON.stringify(responseItem.additional_data, null, 1)}
                              </pre>
                            ) : 'No additional data'}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  
                  {response.filtered_responses.length > 15 && (
                    <Message>
                      <p>Showing first 15 of {response.filtered_responses.length} total responses</p>
                    </Message>
                  )}
                </>
              ) : response.filtered_responses ? (
                <Message info>
                  <Message.Header>Response Data Structure</Message.Header>
                  <p>Response data is available but in a different format:</p>
                  <p><strong>Data Type:</strong> {Array.isArray(response.filtered_responses) ? 'Array' : typeof response.filtered_responses}</p>
                  <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(response.filtered_responses, null, 2)}
                  </pre>
                </Message>
              ) : null}
            </ReportSection>
          )}

          {response.summary && (
            <ReportSection>
              <Header as="h4">Summary Statistics</Header>
              <Grid columns={4} divided>
                <Grid.Row>
                  <Grid.Column>
                    <Statistic size="small">
                      <Statistic.Value>{response.summary.total_categories || 0}</Statistic.Value>
                      <Statistic.Label>Total Categories</Statistic.Label>
                    </Statistic>
                  </Grid.Column>
                  <Grid.Column>
                    <Statistic size="small">
                      <Statistic.Value>{response.summary.total_options || 0}</Statistic.Value>
                      <Statistic.Label>Total Options</Statistic.Label>
                    </Statistic>
                  </Grid.Column>
                  <Grid.Column>
                    <Statistic size="small">
                      <Statistic.Value>{response.summary.avg_response_rate || 0}%</Statistic.Value>
                      <Statistic.Label>Avg Response Rate</Statistic.Label>
                    </Statistic>
                  </Grid.Column>
                  <Grid.Column>
                    <Statistic size="small">
                      <Statistic.Value>{response.summary.completion_time || 'N/A'}</Statistic.Value>
                      <Statistic.Label>Avg Completion Time</Statistic.Label>
                    </Statistic>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </ReportSection>
          )}
        </Card.Content>
      </Card>
    );
  };

  const renderDetailedAnalysis = (analysis) => {
    if (!analysis || !analysis.priority_analysis) {
      return <Message info>No detailed analysis available</Message>;
    }

    const { priority_analysis, insights, participation_summary } = analysis;

    return (
      <AnalysisWrapper>
        {/* Summary Section */}
        <SummarySection>
          <Header as="h5">Participation Summary</Header>
          <Grid columns={3} divided>
            <Grid.Row>
              <Grid.Column>
                <Statistic size="mini">
                  <Statistic.Value>{participation_summary?.total_invited || 0}</Statistic.Value>
                  <Statistic.Label>Total Invited</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini">
                  <Statistic.Value>{participation_summary?.total_responded || 0}</Statistic.Value>
                  <Statistic.Label>Total Responded</Statistic.Label>
                </Statistic>
              </Grid.Column>
              <Grid.Column>
                <Statistic size="mini">
                  <Statistic.Value>{participation_summary?.overall_response_rate || 0}%</Statistic.Value>
                  <Statistic.Label>Response Rate</Statistic.Label>
                </Statistic>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </SummarySection>

        {/* Insights Section */}
        {insights && insights.length > 0 && (
          <InsightsSection>
            <Header as="h5">Key Insights</Header>
            {insights.map((insight, index) => (
              <Message
                key={index}
                warning={insight.priority === 'medium'}
                negative={insight.priority === 'high'}
                info={insight.priority === 'low'}
              >
                <Message.Header>{insight.type.replace(/_/g, ' ').toUpperCase()}</Message.Header>
                <p>{insight.message}</p>
                {insight.category && <p><strong>Category:</strong> {insight.category}</p>}
              </Message>
            ))}
          </InsightsSection>
        )}

        {/* Priority Analysis */}
        {Object.keys(priority_analysis).map(priority => (
          <PrioritySection key={priority}>
            <Header as="h5">
              {priority.charAt(0).toUpperCase() + priority.slice(1)} Categories
              <PriorityLabel priority={priority}>
                {priority}
              </PriorityLabel>
            </Header>
            {priority_analysis[priority].map((category, categoryIndex) => (
              <CategoryCard key={categoryIndex}>
                <Card.Content>
                  <Card.Header>
                    {category.category_name}
                    <span style={{ float: 'right', fontSize: '0.8em', color: '#666' }}>
                      {category.total_options} options
                    </span>
                  </Card.Header>
                  <Table compact size="small">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Option</Table.HeaderCell>
                        <Table.HeaderCell>Requested</Table.HeaderCell>
                        <Table.HeaderCell>Responded</Table.HeaderCell>
                        <Table.HeaderCell>Rate</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {category.option_performance?.slice(0, 10).map((option, optionIndex) => {
                        return (
                          <Table.Row key={optionIndex}>
                            <Table.Cell>{option.option_name}</Table.Cell>
                            <Table.Cell>{option.requested}</Table.Cell>
                            <Table.Cell>{option.responded}</Table.Cell>
                            <Table.Cell>
                              <Progress 
                                percent={option.response_rate} 
                                size="tiny"
                                success={option.response_rate >= 80}
                                warning={option.response_rate >= 60 && option.response_rate < 80}
                                error={option.response_rate < 60}
                              />
                              {option.response_rate}%
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </Card.Content>
              </CategoryCard>
            ))}
          </PrioritySection>
        ))}
      </AnalysisWrapper>
    );
  };

  const renderCategoryDeepDiveReport = () => {
    if (!category_deep_dive || !selectedOptionInfo) {
      return (          <Message info>
            <Message.Header>No Category Deep Dive Selected</Message.Header>
            <p>Click "Select This Category" on any option from the Default Report tab to view detailed analysis for that specific option.</p>
            <Button 
              primary
              onClick={() => setActiveTab(0)}
              content="Go to Default Report"
              icon="arrow left"
            />
          </Message>
      );
    }

    // Extract data from the actual structure based on your Redux store
    const { category_breakdown, detailed_analysis, filtered_responses, summary } = category_deep_dive;
    
    // Debug logging
    console.log('Category Deep Dive Data:', category_deep_dive);
    console.log('Category Breakdown:', category_breakdown);
    console.log('Summary:', summary);
    
    return (
      <div>
        <Button 
          basic
          onClick={() => setActiveTab(0)}
          content="← Back to Default Report"
          style={{ marginBottom: '15px' }}
        />
        
        <Card fluid>
          <Card.Content>
            <Card.Header>
              Category Deep Dive: {selectedOptionInfo.optionName}
              <Label color="blue" style={{ marginLeft: '10px' }}>
                {selectedOptionInfo.categoryName}
              </Label>
            </Card.Header>
            <Card.Meta>
              Analysis for employees who selected "{selectedOptionInfo.optionName}" in the {selectedOptionInfo.categoryName} category
            </Card.Meta>
          </Card.Content>
          <Card.Content>
            <Message positive>
              <Message.Header>Selected Option Analysis</Message.Header>
              <p>This report shows data specifically for employees who selected <strong>"{selectedOptionInfo.optionName}"</strong>.</p>
              <p><strong>API Endpoint:</strong> <code>GET /api/support/v1/debrief-schedule/{debriefId}/category-deep-dive/</code></p>
              <p><strong>Option ID:</strong> <code>{selectedOptionInfo.optionId}</code></p>
            </Message>
            
            <Grid columns={3} divided>
              <Grid.Row>
                <Grid.Column>
                  <Statistic size="small">
                    <Statistic.Value>{summary?.total_options || 0}</Statistic.Value>
                    <Statistic.Label>Total Options</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size="small">
                    <Statistic.Value>{summary?.total_requested || 0}</Statistic.Value>
                    <Statistic.Label>Total Requested</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size="small">
                    <Statistic.Value>{summary?.total_responded || 0}</Statistic.Value>
                    <Statistic.Label>Total Responded</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid.Row>
            </Grid>

            <Header as="h4">Category Breakdown for Selected Option</Header>
            {category_breakdown && category_breakdown.length > 0 ? (
              <>
                <Message positive>
                  <Message.Header>Category Performance for "{selectedOptionInfo.optionName}"</Message.Header>
                  <p>This shows how the selected option performed across different categories in the survey.</p>
                </Message>
                
                {category_breakdown.map((category, categoryIndex) => {
                  const hasOptions = category.options && category.options.length > 0;
                  
                  // Calculate category totals from options
                  const categoryTotalRequested = hasOptions ? 
                    category.options.reduce((sum, option) => sum + (option.requested_count || 0), 0) : 0;
                  const categoryTotalResponded = hasOptions ? 
                    category.options.reduce((sum, option) => sum + (option.responded_count || 0), 0) : 0;
                  const categoryResponseRate = categoryTotalRequested > 0 ? 
                    ((categoryTotalResponded / categoryTotalRequested) * 100).toFixed(2) : 0;
                  
                  return (
                    <div key={categoryIndex} style={{ marginBottom: '30px', border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px' }}>
                      <Header as="h5" style={{ marginBottom: '15px', color: '#2185d0' }}>
                        {category.category_name}
                        <span style={{ float: 'right' }}>
                          <PriorityLabel priority={category.priority}>
                            {category.priority}
                          </PriorityLabel>
                        </span>
                      </Header>
                      
                      {/* Category Summary */}
                      <Grid columns={3} divided style={{ marginBottom: '20px' }}>
                        <Grid.Row>
                          <Grid.Column>
                            <Statistic size="mini">
                              <Statistic.Value>{categoryTotalRequested}</Statistic.Value>
                              <Statistic.Label>Total Requested</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                          <Grid.Column>
                            <Statistic size="mini">
                              <Statistic.Value>{categoryTotalResponded}</Statistic.Value>
                              <Statistic.Label>Total Responded</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                          <Grid.Column>
                            <Statistic size="mini">
                              <Statistic.Value>{categoryResponseRate}%</Statistic.Value>
                              <Statistic.Label>Response Rate</Statistic.Label>
                            </Statistic>
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>

                      <Progress 
                        percent={parseFloat(categoryResponseRate)} 
                        size="small"
                        success={categoryResponseRate >= 80}
                        warning={categoryResponseRate >= 60 && categoryResponseRate < 80}
                        error={categoryResponseRate < 60}
                        style={{ marginBottom: '20px' }}
                      />

                      {/* Individual Options */}
                      {hasOptions ? (
                        <div>
                          <Header as="h6" style={{ marginBottom: '10px' }}>
                            Options in this Category ({category.options.length} options)
                          </Header>
                          <Table compact size="small">
                            <Table.Header>
                              <Table.Row>
                                <Table.HeaderCell>Option ID</Table.HeaderCell>
                                <Table.HeaderCell>Option Name</Table.HeaderCell>
                                <Table.HeaderCell>Requested</Table.HeaderCell>
                                <Table.HeaderCell>Responded</Table.HeaderCell>
                                <Table.HeaderCell>Response Rate</Table.HeaderCell>
                                <Table.HeaderCell>Status</Table.HeaderCell>
                              </Table.Row>
                            </Table.Header>
                            <Table.Body>
                              {category.options.map((option, optionIndex) => {
                                const optionId = option.option_id || option.id || option.uuid || `${category.category_id}_${optionIndex}`;
                                const responseRate = option.response_rate || 0;
                                const isSelectedOption = optionId === selectedOptionInfo.optionId;
                                
                                return (
                                  <Table.Row key={optionIndex} positive={isSelectedOption}>
                                    <Table.Cell>
                                      <code style={{ fontSize: '11px' }}>{optionId}</code>
                                    </Table.Cell>
                                    <Table.Cell>
                                      <strong>{option.option_name}</strong>
                                      {isSelectedOption && (
                                        <Label color="blue" size="mini" style={{ marginLeft: '10px' }}>
                                          Selected
                                        </Label>
                                      )}
                                    </Table.Cell>
                                    <Table.Cell>{option.requested_count || 0}</Table.Cell>
                                    <Table.Cell>{option.responded_count || 0}</Table.Cell>
                                    <Table.Cell>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Progress 
                                          percent={responseRate} 
                                          size="tiny"
                                          success={responseRate >= 80}
                                          warning={responseRate >= 60 && responseRate < 80}
                                          error={responseRate < 60}
                                          style={{ margin: 0, minWidth: '100px' }}
                                        />
                                        <span>{responseRate}%</span>
                                      </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                      {isSelectedOption ? (
                                        <Label color="blue" size="mini">
                                          Current Analysis
                                        </Label>
                                      ) : option.requested_count > 0 ? (
                                        <Button 
                                          size="mini" 
                                          basic
                                          onClick={() => {
                                            handleOptionDeepDive(
                                              optionId, 
                                              option.option_name, 
                                              category.category_name
                                            );
                                          }}
                                        >
                                          Switch to This
                                        </Button>
                                      ) : (
                                        <span style={{ fontSize: '11px', color: '#999' }}>No data</span>
                                      )}
                                    </Table.Cell>
                                  </Table.Row>
                                );
                              })}
                            </Table.Body>
                          </Table>
                        </div>
                      ) : (
                        <Message info>
                          <p>No options available for this category.</p>
                        </Message>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <Message info>No category breakdown data available for the selected option</Message>
            )}

            {detailed_analysis && (
              <>
                <Header as="h4">Detailed Analysis</Header>
                <DetailedAnalysisContainer>
                  {renderDetailedAnalysis(detailed_analysis)}
                </DetailedAnalysisContainer>
              </>
            )}

            {filtered_responses && (
              <>
                <Header as="h4">Filtered Responses</Header>
                {Array.isArray(filtered_responses) && filtered_responses.length > 0 ? (
                  <>
                    <Message info>
                      <Message.Header>Employee Responses for Selected Option</Message.Header>
                      <p>Showing {filtered_responses.length} responses from employees who selected "{selectedOptionInfo.optionName}"</p>
                    </Message>
                    
                    <Table celled striped>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Employee</Table.HeaderCell>
                          <Table.HeaderCell>Response Date</Table.HeaderCell>
                          <Table.HeaderCell>Additional Data</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {filtered_responses.slice(0, 10).map((response, index) => (
                          <Table.Row key={index}>
                            <Table.Cell>
                              {response.employee_name || response.user_id || `Employee ${index + 1}`}
                            </Table.Cell>
                            <Table.Cell>
                              {response.created_at || response.response_date || 'N/A'}
                            </Table.Cell>
                            <Table.Cell>
                              {response.additional_data ? (
                                <pre style={{ fontSize: '11px', margin: 0 }}>
                                  {JSON.stringify(response.additional_data, null, 1)}
                                </pre>
                              ) : 'No additional data'}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                    
                    {filtered_responses.length > 10 && (
                      <Message>
                        <p>Showing first 10 of {filtered_responses.length} total responses</p>
                      </Message>
                    )}
                  </>
                ) : (
                  <Message info>
                    <Message.Header>Response Data</Message.Header>
                    <p>Filtered response data for the selected option:</p>
                    <p><strong>Data Type:</strong> {Array.isArray(filtered_responses) ? 'Array' : typeof filtered_responses}</p>
                    <pre style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                      {JSON.stringify(filtered_responses, null, 2)}
                    </pre>
                  </Message>
                )}
              </>
            )}
          </Card.Content>
        </Card>
      </div>
    );
  };

  if (pending) {
    return <Loader active>Loading Deep Dive Report...</Loader>;
  }

  const panes = [
    {
      menuItem: { 
        key: 'default', 
        icon: 'chart bar', 
        content: 'Default Deep Dive Report' 
      },
      render: () => (
        <Tab.Pane>
          <Message info>
            <Message.Header>Default Deep Dive Report</Message.Header>
            <p>This shows the overall analysis for all options. Data from <code>GET /api/support/v1/debrief-schedule/{debriefId}/deep-dive-report/</code></p>
            <p>Click "Select This Category" on any specific option to analyze employees who selected that particular option.</p>
          </Message>
          
          {renderDeepDiveReport()}
        </Tab.Pane>
      )
    },
    {
      menuItem: { 
        key: 'category', 
        icon: 'search', 
        content: selectedOptionInfo ? `Category Deep Dive: ${selectedOptionInfo.optionName}` : 'Category Deep Dive'
      },
      render: () => (
        <Tab.Pane>
          {renderCategoryDeepDiveReport()}
        </Tab.Pane>
      )
    }
  ];

  return (
    <DeepDiveContainer>
      <SectionWrapper>
        <Header as="h2">Deep Dive Analysis</Header>
        
        {/* Organization Filter */}
        <OrganizationSelect 
          setResetPageLoad={setResetPageLoad}
          handleOrganizationChange={handleOrganizationChange}
          handleResetQueryPArams={handleResetQueryParams}
        />
        
        <SectionHeader>Survey Structure</SectionHeader>
        {renderSurveyStructure()}
        
        <Tab 
          panes={panes} 
          activeIndex={activeTab}
          onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex)}
        />
      </SectionWrapper>
    </DeepDiveContainer>
  );
};

export default DeepDiveReport;

// Styled Components
const DeepDiveContainer = styled.div`
  margin: 20px 0;
`;

const SectionWrapper = styled.div`
  margin-bottom: 30px;
`;

const SectionHeader = styled(Header)`
  margin-top: 25px !important;
  margin-bottom: 15px !important;
`;

const ReportSection = styled.div`
  margin: 20px 0;
`;

const PriorityLabel = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  color: white;
  font-size: 12px;
  font-weight: bold;
  background-color: ${props => {
    switch (props.priority) {
      case 'primary': return '#2185d0';
      case 'secondary': return '#00b5ad';
      case 'tertiary': return '#f2711c';
      default: return '#767676';
    }
  }};
`;

const DetailedAnalysisContainer = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const AnalysisWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const SummarySection = styled.div`
  background-color: #fff;
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
`;

const InsightsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PrioritySection = styled.div`
  margin-bottom: 30px;
`;

const CategoryCard = styled(Card)`
  margin-bottom: 15px !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
`;

const CategoryStructureCard = styled(Card)`
  margin-bottom: 20px !important;
  border: 1px solid #e9ecef !important;
  box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
  
  .ui.card > .content > .header {
    color: #2185d0 !important;
  }
`;

const SurveyStructureHeader = styled.div`
  margin-bottom: 20px;
  padding: 20px 0;
  border-bottom: 2px solid #e9ecef;
`;

const SurveyStructureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  width: 100%;
`;